import { PayloadAction } from '@reduxjs/toolkit';
import { put, call, takeLatest, takeEvery } from 'redux-saga/effects';

import {
  getDeviceCategories,
  getDeviceMapping,
  postDeviceMapping
} from '../api';

import {
  getDeviceCategoriesFailure,
  getDeviceCategoriesRequest,
  getDeviceCategoriesSuccess,
  postDeviceMappingFailure,
  postDeviceMappingRequest,
  postDeviceMappingUpdate,
  getDeviceMappingRequest,
  getDeviceMappingSuccess,
  getDeviceMappingFailure
} from '../features/devices/devices-slice';

import {
  DeviceCategory,
  PostDeviceMappingRequestPayload,
  PostDeviceMappingRequestResponse,
  GetDeviceMappingRequestResponse,
  GetDeviceMappingRequestPayload
} from '../types/devices';

import { unmarshalError } from '../utils/common';

function* handleGetDeviceCategoriesRequest() {
  try {
    const response: DeviceCategory[] = yield call(getDeviceCategories);
    yield put(getDeviceCategoriesSuccess(response));
  } catch (error) {
    const message = 'api-errors.devices.get-device-categories.generic-error';
    const errorRet = unmarshalError(message, error);
    yield put(getDeviceCategoriesFailure(errorRet));
  }
}

function* handlePostDeviceMappingRequest(
  action: PayloadAction<{
    type: string;
    payload: PostDeviceMappingRequestPayload;
  }>
) {
  try {
    const { device_type, device_sub_type } = action.payload.payload;
    const response: PostDeviceMappingRequestResponse = yield call(
      postDeviceMapping,
      action.payload.payload
    );
    yield put(
      postDeviceMappingUpdate({
        id: response.id,
        device_type,
        device_sub_type
      })
    );
  } catch (error) {
    const message = 'api-errors.devices.device-mapping.generic-error';
    const errorRet = unmarshalError(message, error);

    if (error?.trace_id) {
      errorRet.trace_id = error.trace_id;
    }
    yield put(
      postDeviceMappingFailure({ type: action.payload.type, error: errorRet })
    );
  }
}

function* handleGetDeviceMappingRequest(
  action: PayloadAction<{
    id: string;
    payload: GetDeviceMappingRequestPayload;
    userEmail?: string;
  }>
) {
  const { payload, userEmail } = action.payload;
  try {
    const response: GetDeviceMappingRequestResponse = yield call(
      getDeviceMapping,
      action.payload
    );
    yield put(
      getDeviceMappingSuccess({
        data: response,
        payload,
        userEmail
      })
    );
  } catch (error) {
    let statusCode: number | undefined;
    if (error?.status) {
      statusCode = error.status;
    }
    const message = 'api-errors.devices.device-mapping.generic-error';
    const errorRet = unmarshalError(message, error);
    if (error?.trace_id) {
      errorRet.trace_id = error.trace_id;
    }
    yield put(
      getDeviceMappingFailure({
        error: { ...errorRet, statusCode },
        device_type: payload.device_type,
        device_sub_type: payload.device_sub_type
      })
    );
  }
}

function* DevicesSaga(): Generator {
  yield takeLatest(
    getDeviceCategoriesRequest.type,
    handleGetDeviceCategoriesRequest
  );
  yield takeEvery(
    postDeviceMappingRequest.type,
    handlePostDeviceMappingRequest
  );
  yield takeEvery(getDeviceMappingRequest.type, handleGetDeviceMappingRequest);
}

export default DevicesSaga;
